import { useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useConsoleApi } from "./useConsoleApi";
import { SEVERITIES, type CaResourcesResp } from "./types";
import { SEV_STYLE, SeverityTag, fmtNum, timeAgo } from "./utils";
import { EmptyState } from "./FindingsTab";


export default function ResourcesTab({ bootId, refreshMs, now, onSelect }: {
    bootId: string;
    refreshMs: number;
    now: number;
    onSelect: (resource: string) => void;
}) {
    const [search, setSearch] = useState('');
    const resp = useConsoleApi<CaResourcesResp>('resources', { boot: bootId }, refreshMs);
    const items = (resp.data?.items ?? []).filter((r) => r.resource.toLowerCase().includes(search.trim().toLowerCase()));

    if (resp.error) return <EmptyState>Failed to load resources: {(resp.error as Error).message}</EmptyState>;
    if (!resp.data) return <EmptyState>Loading…</EmptyState>;
    if (!resp.data.items.length) return <EmptyState>No resource has findings in this boot.</EmptyState>;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <Input
                    className="h-8 w-60"
                    placeholder="Filter resources…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <span className="ml-auto text-xs text-muted-foreground">
                    Click a resource to see its findings.
                </span>
            </div>
            <div className="overflow-x-auto rounded-lg border bg-card">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-left text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                            <th className="px-3 py-2">Resource</th>
                            <th className="px-3 py-2">Worst</th>
                            {SEVERITIES.map((sev) => (
                                <th key={sev} className={cn('px-2 py-2 text-right', SEV_STYLE[sev].text)}>{sev.slice(0, 4)}</th>
                            ))}
                            <th className="px-3 py-2 text-right">Findings</th>
                            <th className="px-3 py-2 text-right">Occurrences</th>
                            <th className="px-3 py-2 text-right">Assets</th>
                            <th className="px-3 py-2">Last seen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((r) => (
                            <tr
                                key={r.resource}
                                onClick={() => onSelect(r.resource)}
                                className="cursor-pointer border-b last:border-b-0 hover:bg-muted/30"
                            >
                                <td className="px-3 py-1.5 font-mono text-xs">
                                    {r.resource === '(server)' ? <span className="italic text-muted-foreground">server</span> : r.resource}
                                    {r.failed && (
                                        <span className="ml-2 rounded-sm border border-red-500/40 px-1 text-2xs font-semibold uppercase text-red-500">
                                            failed
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5">{r.worst ? <SeverityTag severity={r.worst} /> : '--'}</td>
                                {SEVERITIES.map((sev) => (
                                    <td key={sev} className={cn('px-2 py-1.5 text-right font-mono text-xs', r.counts[sev] ? SEV_STYLE[sev].text : 'text-muted-foreground/40')}>
                                        {r.counts[sev] || '·'}
                                    </td>
                                ))}
                                <td className="px-3 py-1.5 text-right font-mono text-xs">{fmtNum(r.findings)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs">{fmtNum(r.occurrences)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs">{r.assets ? fmtNum(r.assets) : '·'}</td>
                                <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.lastSeen ? timeAgo(r.lastSeen, now) : '--'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
